/**
 * Signal waveform viewer (PRD §5.5 U-03).
 *
 * Renders a 30-second time-domain waveform from a JSON float array fetched
 * via signed URL. Built on wavesurfer.js. Falls back gracefully if the
 * `signal_window_ref` is null (PRD: "Signal snapshot not available").
 *
 * Acceptance criteria:
 *   • renders in < 2 seconds from signed URL
 *   • zoom 2× / 5× / 10× via toolbar
 *   • playhead marker at event timestamp within window
 *   • null ref shows fallback, no error
 *
 * Day 5 scope: standalone component with a `signedUrl` prop. The wiring to
 * the incident detail page lands in Week 3.
 */

import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";

export interface WaveformViewerProps {
  /** Signed S3/MinIO URL pointing at a SignalWindowPayload JSON document. */
  signedUrl?: string | null;
  /** ISO 8601 timestamp of the event within the window (for the playhead). */
  eventTimestamp?: string | null;
}

interface SignalWindowPayload {
  sample_rate_hz: number;
  duration_seconds: number;
  samples: number[];
  start_timestamp: string;
  event_id: string;
}

const ZOOM_LEVELS = [1, 2, 5, 10] as const;
type Zoom = (typeof ZOOM_LEVELS)[number];

export function WaveformViewer({ signedUrl, eventTimestamp }: WaveformViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    signedUrl ? "loading" : "idle",
  );
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<Zoom>(1);

  useEffect(() => {
    if (!signedUrl || !containerRef.current) return;

    let cancelled = false;
    setStatus("loading");
    setError(null);

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#475569",
      progressColor: "#14b8a6",
      cursorColor: "#f97316",
      barWidth: 1,
      height: 120,
      normalize: true,
      interact: true,
    });
    wsRef.current = ws;

    (async () => {
      try {
        const res = await fetch(signedUrl);
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        const payload = (await res.json()) as SignalWindowPayload;
        if (cancelled) return;

        // Convert JSON float array → wavesurfer peaks via a synthetic
        // PCM blob. wavesurfer accepts a Float32Array as `peaks` for
        // pre-decoded waveforms.
        const samples = new Float32Array(payload.samples);
        await ws.load("", [samples], payload.duration_seconds);
        if (cancelled) return;

        // Place playhead at the event timestamp inside the window
        if (eventTimestamp) {
          const start = new Date(payload.start_timestamp).getTime();
          const evt = new Date(eventTimestamp).getTime();
          const offsetSeconds = Math.max(
            0,
            Math.min(payload.duration_seconds, (evt - start) / 1000),
          );
          ws.setTime(offsetSeconds);
        }
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "unknown error");
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      ws.destroy();
      wsRef.current = null;
    };
  }, [signedUrl, eventTimestamp]);

  useEffect(() => {
    wsRef.current?.zoom(zoom * 50);
  }, [zoom]);

  if (!signedUrl) {
    return (
      <div
        className="text-slate-500 text-sm italic"
        data-testid="waveform-fallback"
      >
        Signal snapshot not available.
      </div>
    );
  }

  return (
    <div data-testid="waveform-viewer">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-slate-400">Zoom:</span>
        {ZOOM_LEVELS.map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => setZoom(z)}
            data-testid={`waveform-zoom-${z}x`}
            className={`text-xs px-2 py-0.5 rounded border ${
              zoom === z
                ? "border-teal-400 text-teal-300"
                : "border-slate-700 text-slate-400 hover:text-slate-200"
            }`}
          >
            {z}×
          </button>
        ))}
        <span
          className="ml-auto text-xs text-slate-500"
          data-testid="waveform-status"
        >
          {status === "loading" && "loading…"}
          {status === "ready" && "ready"}
          {status === "error" && `error: ${error}`}
        </span>
      </div>
      <div ref={containerRef} className="w-full" />
    </div>
  );
}
